import React, { useMemo, useState } from 'react';
import { FaDownload, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { SearchableSelect } from './SearchableSelect';
import { useGetLicensesQuery } from '../../redux/licenceApi';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { authFetch } from '../../utils/authFetch';
import { useAuth } from '../../context/AuthContext';
import TablePagination from './TablePagination';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';

function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fileNameFromUrl(url) {
  if (!url) return 'certificate';
  const last = String(url).split('?')[0].split('/').filter(Boolean).pop() || 'certificate';
  return decodeURIComponent(last);
}

function mapLicenseRow(raw) {
  const certificateUrl = resolveAssetUrl(raw?.certificate_url);
  return {
    id: raw?.id,
    name: raw?.name || '',
    locationName: raw?.location_name || '',
    applicationId: raw?.application_id || '',
    licenceCategory: raw?.licence_category || '',
    issueAuthority: raw?.issue_authority || '',
    level: raw?.level || '',
    status: raw?.status != null ? String(raw.status) : '',
    validity: raw?.validity || '',
    renewalDate: raw?.renewal_date || '',
    triggerMonth: raw?.trigger_month != null ? String(raw.trigger_month) : '',
    cc: raw?.cc || '',
    too: raw?.too || '',
    remark: raw?.remark || '',
    createdAt: raw?.created_at || '',
    updatedAt: raw?.updated_at || '',
    certificateUrl,
    certificateFileName:
      fileNameFromUrl(certificateUrl) ||
      `${raw?.application_id || raw?.name || 'certificate'}`,
  };
}

async function downloadCertificate(url, fileName) {
  try {
    const res = await authFetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName || 'certificate';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'certificate';
    a.rel = 'noreferrer noopener';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

const EXPIRY_FILTER_OPTIONS = [
  { label: '90-60 Days', value: '90_60' },
  { label: '60-30 Days', value: '60_30' },
  { label: 'Within 30 Days', value: 'within_30' },
];

function AdminLicencesList() {
  const { canAction } = useAuth();
  const canView = canAction('licences', 'view');
  const [expiryFilter, setExpiryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const pageSize = DEFAULT_PAGE_SIZE;
  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    if (expiryFilter) params.expiry_filter = expiryFilter;
    return params;
  }, [page, pageSize, search, expiryFilter]);
  const { data: licensesRes, isFetching, isLoading, error, refetch } = useGetLicensesQuery(listQuery);
  const [viewRow, setViewRow] = useState(null);

  const rows = useMemo(() => {
    const list = licensesRes?.data || licensesRes || [];
    const safe = Array.isArray(list) ? list : [];
    return safe
      .slice()
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map(mapLicenseRow);
  }, [licensesRes]);

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    licensesRes,
    { page, pageSize }
  );
  const pageItems = rows;

  const handleDownloadCertificate = async (row) => {
    if (!row?.certificateUrl) {
      toast.error('No certificate available to download.');
      return;
    }
    try {
      await downloadCertificate(row.certificateUrl, row.certificateFileName);
      toast.success('Certificate downloaded.');
    } catch {
      toast.error('Failed to download certificate.');
    }
  };

  const detailFields = viewRow
    ? [
        { label: 'Application ID', value: viewRow.applicationId },
        { label: 'Licence Name', value: viewRow.name },
        { label: 'Location', value: viewRow.locationName },
        { label: 'Licence Category', value: viewRow.licenceCategory },
        { label: 'Issue Authority', value: viewRow.issueAuthority },
        { label: 'Level', value: viewRow.level },
        { label: 'Validity', value: viewRow.validity },
        { label: 'Renewal Date', value: formatDate(viewRow.renewalDate) },
        { label: 'Trigger Month', value: viewRow.triggerMonth },
        { label: 'CC', value: viewRow.cc },
        { label: 'TOO', value: viewRow.too },
        { label: 'Remark', value: viewRow.remark },
        { label: 'Created At', value: formatDate(viewRow.createdAt) },
        { label: 'Updated At', value: formatDate(viewRow.updatedAt) },
      ]
    : [];

  return (
    <DashboardLayout title="Licences">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex w-full flex-col gap-3 md:max-w-2xl md:flex-row md:items-center">
              <div className="relative w-full min-w-0 md:max-w-xs">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search licences"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
              <label className="flex w-full min-w-0 flex-col gap-1.5 text-sm text-gray-700 md:flex-row md:items-center md:gap-2 md:w-56 md:shrink-0">
                <span className="shrink-0 font-medium">Filter By:</span>
                <div className="w-full min-w-0">
                  <SearchableSelect
                    name="expiryFilter"
                    value={expiryFilter}
                    onChange={(e) => {
                      setExpiryFilter(e.target.value);
                      setPage(1);
                    }}
                    options={EXPIRY_FILTER_OPTIONS}
                    placeholder="All Days"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </div>
              </label>
            </div>
          </div>

          <ApiLoadState
            isLoading={isLoading}
            isFetching={isFetching}
            hasData={rows.length > 0}
            error={error}
            refetch={refetch}
            message="Loading licences…"
            errorMessage={error?.data?.message || 'Unable to load licences.'}
            className="py-6 md:hidden"
          />
          {!error && (rows.length > 0 || (!isLoading && !isFetching)) && pageItems.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500 md:hidden">No licences found.</p>
          ) : !error && (rows.length > 0 || (!isLoading && !isFetching)) && pageItems.length > 0 ? (
            <ul className="mb-4 space-y-3 md:hidden">
              {pageItems.map((r, idx) => (
                <li
                  key={r.id ?? idx}
                  className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">
                        #{(currentPage - 1) * pageSize + idx + 1}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {r.name || '—'}
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        <span className="text-gray-500">Location: </span>
                        <span className="font-medium text-gray-800">
                          {r.locationName || '—'}
                        </span>
                      </p>
                      <p className="mt-2 break-all text-xs text-gray-600">
                        <span className="text-gray-500">App ID: </span>
                        <span className="font-medium text-gray-800">
                          {r.applicationId || '—'}
                        </span>
                      </p>
                      <div className="mt-2 space-y-1 text-xs text-gray-600">
                        <p>
                          Category:{' '}
                          <span className="font-medium text-gray-800">
                            {r.licenceCategory || '—'}
                          </span>
                        </p>
                        <p>
                          Renewal:{' '}
                          <span className="font-medium text-gray-800">
                            {formatDate(r.renewalDate)}
                          </span>
                        </p>
                        <p>
                          Status:{' '}
                          <span className="font-medium text-gray-800">{r.status || '—'}</span>
                        </p>
                      </div>
                    </div>
                    {canView ? (
                      <button
                        type="button"
                        onClick={() => setViewRow(r)}
                        className="shrink-0 rounded-lg p-1.5 text-red-700 hover:bg-white"
                        aria-label="View licence"
                      >
                        <FaEye className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Sr. No.
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Licence Name
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Location
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Application ID
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Category
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Renewal Date
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <ApiLoadTableRow
                  colSpan={7}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  hasData={rows.length > 0}
                  error={error}
                  refetch={refetch}
                  message="Loading licences..."
                  errorMessage={error?.data?.message || 'Unable to load licences.'}
                />
                {!error && (rows.length > 0 || (!isLoading && !isFetching)) &&
                  pageItems.map((r, idx) => (
                    <tr key={r.id ?? idx} className="hover:bg-amber-50/40">
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {(currentPage - 1) * pageSize + idx + 1}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {r.name || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {r.locationName || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {r.applicationId || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {r.licenceCategory || '—'}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                        {formatDate(r.renewalDate)}
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5">
                        {canView ? (
                          <button
                            type="button"
                            onClick={() => setViewRow(r)}
                            className="rounded-md p-1 text-red-700"
                            aria-label="View licence"
                            title="View"
                          >
                            <FaEye className="h-5 w-5" />
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                {!error && !isLoading && !isFetching && pageItems.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-8 text-center text-sm text-gray-500"
                    >
                      No licences found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <TablePagination
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={effectivePageSize}
          />
        </div>
      </div>

      {viewRow && (
        <div
          className="fixed inset-0 z-40 flex justify-end bg-black/30"
          onClick={() => setViewRow(null)}
          role="presentation"
        >
          <div
            className="flex h-full w-full min-w-0 max-w-full flex-col bg-white shadow-2xl sm:max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
              <h2 className="text-lg font-semibold text-brand">Licence Details</h2>
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                onClick={() => setViewRow(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-10 sm:px-6 sm:pb-8">
              <div className="space-y-2 sm:grid sm:grid-cols-2 sm:gap-3 sm:space-y-0">
                {detailFields.map(({ label, value }) => (
                  <div
                    key={label}
                    className={`rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 ${
                      label === 'Application ID' || label === 'Remark' ? 'sm:col-span-2' : ''
                    }`}
                  >
                    <p className="text-xs text-gray-500">{label}</p>
                    <p
                      className={`mt-0.5 text-sm font-medium text-gray-900 ${
                        label === 'Application ID' ? 'break-all' : ''
                      }`}
                    >
                      {value || '—'}
                    </p>
                  </div>
                ))}
              </div>

              {viewRow.certificateUrl ? (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => handleDownloadCertificate(viewRow)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white sm:w-auto"
                  >
                    <FaDownload className="h-4 w-4" />
                    Download Certificate
                  </button>
                </div>
              ) : (
                <p className="mt-4 border-t border-gray-100 pt-4 text-sm font-semibold text-brand">
                  No certificate uploaded.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default AdminLicencesList;
